/**
 * IT-OFFBOARD-EVIDENCE-001 — Offboarding case detail modal (IT side).
 * Per-asset findings + recovery value, IT notes, evidence attachments (1 MB),
 * Escalate to HR, guarded Mark Cleared, and the permanent audit history.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { X, Upload, Download, Send, CheckCircle, StickyNote, History, Package } from 'lucide-react';
import { formatDateTimeET, formatDateOnly } from '../../utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const token = () => localStorage.getItem('token');

const api = async (endpoint, options = {}) => {
  const res = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}`, ...options.headers },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(err.detail || 'Request failed');
  }
  return res.json();
};

const STATUS_LABELS = {
  pending: 'Pending', in_progress: 'In Progress', awaiting_asset_return: 'Awaiting Asset Return',
  escalated_to_hr: 'Escalated to HR', cleared: 'Cleared', cancelled: 'Cancelled',
};
const FINDING_OPTIONS = [
  ['', '— set status —'], ['returned', 'Returned'], ['not_returned', 'Not Returned'],
  ['lost', 'Lost'], ['outstanding', 'Outstanding'],
];
const BAD = ['not_returned', 'lost', 'outstanding'];
const fmtDT = (d) => d ? formatDateTimeET(d) + ' ET' : '—';
const money = (v) => (v === null || v === undefined || v === '') ? '—' : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

const AssetFindingRow = ({ asset, finding, onSave, disabled }) => {
  const [status, setStatus] = useState(finding?.status || '');
  const [remark, setRemark] = useState(finding?.remark || '');
  const [value, setValue] = useState(finding?.recovery_value ?? '');
  const dirty = status !== (finding?.status || '') || remark !== (finding?.remark || '') || String(value) !== String(finding?.recovery_value ?? '');
  return (
    <tr data-testid={`offb-asset-row-${asset.id}`}>
      <td>
        <div style={{ fontWeight: 600 }}>{asset.name}</div>
        <div style={{ fontSize: 11.5, color: '#64748b' }}>{asset.asset_tag}{asset.serial_number ? ` · S/N ${asset.serial_number}` : ''}</div>
      </td>
      <td style={{ fontSize: 12.5 }}>{money(asset.purchase_cost)}</td>
      <td>
        <select value={status} onChange={e => setStatus(e.target.value)} disabled={disabled}
          className="offb-input" data-testid={`offb-finding-status-${asset.id}`}>
          {FINDING_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </td>
      <td>
        <input type="number" min="0" step="0.01" value={value} placeholder="0.00" disabled={disabled}
          onChange={e => setValue(e.target.value)} className="offb-input" style={{ width: 100 }}
          data-testid={`offb-finding-value-${asset.id}`} />
      </td>
      <td>
        <input type="text" value={remark} placeholder="Remark…" maxLength={2000} disabled={disabled}
          onChange={e => setRemark(e.target.value)} className="offb-input" style={{ width: '100%' }}
          data-testid={`offb-finding-remark-${asset.id}`} />
      </td>
      <td>
        <button className="btn-small" disabled={!status || !dirty || disabled}
          onClick={() => onSave(asset.id, { status, remark, recovery_value: value === '' ? null : Number(value) })}
          data-testid={`offb-finding-save-${asset.id}`}>Save</button>
      </td>
    </tr>
  );
};

export default function OffboardingCaseModal({ caseId, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [forcePanel, setForcePanel] = useState(false);
  const [forceReason, setForceReason] = useState('');

  const load = useCallback(async () => {
    try { setDetail(await api(`/api/it-offboarding/cases/${caseId}/detail`)); }
    catch (e) { toast.error(e.message); }
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  if (!caseId) return null;
  const c = detail?.case;
  const findings = c?.asset_findings || {};
  const badFindings = Object.values(findings).filter(f => BAD.includes(f.status));
  const closed = c && ['cleared', 'cancelled'].includes(c.clearance_status);
  const canClear = c && (badFindings.length === 0 || c.clearance_status === 'escalated_to_hr');

  const saveFinding = async (assetId, payload) => {
    try {
      const r = await api(`/api/it-offboarding/cases/${caseId}/assets/${assetId}/finding`, { method: 'PUT', body: JSON.stringify(payload) });
      toast.success(`Finding saved — case: ${STATUS_LABELS[r.clearance_status] || r.clearance_status}`);
      load(); onChanged?.();
    } catch (e) { toast.error(e.message); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api(`/api/it-offboarding/cases/${caseId}/notes`, { method: 'POST', body: JSON.stringify({ note }) });
      setNote(''); toast.success('Note added'); load();
    } catch (e) { toast.error(e.message); }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > 1024 * 1024) { toast.error('File too large — maximum size is 1 MB'); return; }
    const form = new FormData();
    form.append('file', file);
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/it-offboarding/cases/${caseId}/attachments`, {
        method: 'POST', headers: { Authorization: `Bearer ${token()}` }, body: form,
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Upload failed'); }
      toast.success('Evidence uploaded'); load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const download = async (fileId, filename) => {
    try {
      const res = await fetch(`${API_URL}/api/it-offboarding/cases/${caseId}/attachments/${fileId}/download`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  const escalate = async () => {
    if (!window.confirm(`Escalate ${badFindings.length} unresolved asset(s) to HR? HR will be notified with the notes, values and evidence attached.`)) return;
    setBusy(true);
    try {
      const r = await api(`/api/it-offboarding/cases/${caseId}/escalate`, { method: 'POST', body: JSON.stringify({}) });
      toast.success(`Escalated to HR — ${r.hr_notified} HR user(s) notified`);
      load(); onChanged?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const outstandingAssets = detail ? detail.assets.filter(a => findings[a.id]?.status !== 'returned') : [];

  // IT-OFFBOARD-FORCE-001: normal clearance never bypasses the outstanding-asset check.
  const markCleared = async () => {
    setBusy(true);
    try {
      const r = await api(`/api/it-offboarding/cases/${caseId}/clear`, { method: 'POST', body: JSON.stringify({ force: false }) });
      toast.success(
        `IT clearance completed — ${r?.assets_released?.length || 0} returned asset(s) unassigned` +
        (badFindings.length ? ' — escalated recovery items remain open for HR' : '')
      );
      load(); onChanged?.();
    } catch (e) {
      if (/not yet confirmed returned/i.test(e.message)) {
        setForcePanel(true);
        toast.error(`${outstandingAssets.length} asset(s) have no confirmed return — review them below`);
      } else toast.error(e.message);
    } finally { setBusy(false); }
  };

  const forceClear = async () => {
    if (forceReason.trim().length < 10) { toast.error('Enter an authorization reason (at least 10 characters)'); return; }
    if (!window.confirm(`Authorize force clearance? ${outstandingAssets.length} asset(s) stay assigned to ${c?.employee_name} and will NOT be marked Available. Your name and reason are recorded permanently.`)) return;
    setBusy(true);
    try {
      const r = await api(`/api/it-offboarding/cases/${caseId}/clear`, { method: 'POST', body: JSON.stringify({ force: true, force_reason: forceReason.trim() }) });
      toast.success(`Force clearance recorded — ${r?.assets_released?.length || 0} released, ${r?.assets_outstanding?.length || 0} left assigned (traceable)`);
      setForcePanel(false); setForceReason('');
      load(); onChanged?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 860, width: '95%' }} data-testid="offboarding-case-modal">
        <div className="modal-header">
          <h3><Package size={16} /> Offboarding — {c?.employee_name || '…'}</h3>
          <button className="modal-close" onClick={onClose} data-testid="offb-modal-close"><X size={18} /></button>
        </div>
        {!detail ? <div className="modal-body">Loading…</div> : (
          <div className="modal-body" style={{ maxHeight: '72vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12.5, marginBottom: 12 }}>
              <span>Status: <strong data-testid="offb-case-status">{STATUS_LABELS[c.clearance_status] || c.clearance_status}</strong></span>
              <span>LWD: <strong>{c.last_working_day ? formatDateOnly(c.last_working_day) : 'not set'}</strong></span>
              <span>Trigger: <strong>{c.trigger_status}</strong></span>
              {c.cleared_at && <span>Cleared: <strong>{fmtDT(c.cleared_at)} by {c.cleared_by_name}</strong></span>}
            </div>

            <h4 style={{ fontSize: 13, margin: '10px 0 6px' }}>Assets & Findings</h4>
            <table className="data-table" style={{ width: '100%', fontSize: 12.5 }} data-testid="offb-assets-table">
              <thead><tr><th>Asset</th><th>Purchase</th><th>Finding</th><th>Recovery $</th><th>Remark</th><th></th></tr></thead>
              <tbody>
                {detail.assets.length === 0 ? (
                  <tr><td colSpan={6} className="empty-state">No assets linked to this employee</td></tr>
                ) : detail.assets.map(a => (
                  <AssetFindingRow key={a.id} asset={a} finding={findings[a.id]} onSave={saveFinding} disabled={closed} />
                ))}
              </tbody>
            </table>

            {!closed && outstandingAssets.length > 0 && (
              <div style={{ marginTop: 8, border: '1px solid #fdba74', background: '#fff7ed', borderRadius: 8, padding: '8px 10px', fontSize: 12.5 }} data-testid="offb-outstanding-box">
                <strong style={{ color: '#9a3412' }}>{outstandingAssets.length} asset(s) without a confirmed return</strong>
                <span style={{ color: '#7c2d12' }}> — they stay assigned to {c.employee_name} and will not be marked Available: </span>
                <span style={{ color: '#7c2d12' }}>{outstandingAssets.map(a => `${a.name} (${a.asset_tag})`).join(', ')}</span>
              </div>
            )}
            {c.force_clearance && (
              <div style={{ marginTop: 8, border: '1px solid #fca5a5', background: '#fef2f2', borderRadius: 8, padding: '8px 10px', fontSize: 12.5 }} data-testid="offb-force-record">
                <strong style={{ color: '#991b1b' }}>Force clearance exception</strong> — authorized by {c.force_clearance.authorized_by_name} ({c.force_clearance.authorized_by_role}) · {fmtDT(c.force_clearance.authorized_at)}
                <div style={{ color: '#7f1d1d' }}>Reason: {c.force_clearance.reason} · {c.force_clearance.outstanding_asset_ids?.length || 0} asset(s) left assigned (traceable)</div>
              </div>
            )}

            <h4 style={{ fontSize: 13, margin: '16px 0 6px', display: 'flex', alignItems: 'center', gap: 5 }}><StickyNote size={13} /> IT Clearance Notes</h4>
            {(c.it_notes || []).map(n => (
              <div key={n.id} style={{ borderLeft: '2px solid #cbd5e1', padding: '4px 0 6px 10px', marginBottom: 4, fontSize: 12.5 }} data-testid="offb-note-item">
                <div>{n.note}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>{n.created_by_name} · {fmtDT(n.created_at)}</div>
              </div>
            ))}
            {!closed && (
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <input className="offb-input" style={{ flex: 1 }} value={note} maxLength={2000}
                  onChange={e => setNote(e.target.value)} placeholder="Add a clearance note…" data-testid="offb-note-input" />
                <button className="btn-small" onClick={addNote} disabled={!note.trim()} data-testid="offb-note-add">Add</button>
              </div>
            )}

            <h4 style={{ fontSize: 13, margin: '16px 0 6px', display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}><Upload size={13} /> Supporting Documents <span style={{ fontWeight: 400, color: '#64748b' }}>(max 1 MB — PDF, images, DOC, XLS, EML/MSG, TXT)</span></h4>
            {(c.attachments || []).map(a => (
              <div key={a.file_id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, padding: '3px 0' }} data-testid="offb-attachment-item">
                <span style={{ flex: 1 }}>{a.filename} <span style={{ color: '#94a3b8' }}>({Math.round(a.file_size / 1024)} KB · {a.uploaded_by_name} · {fmtDT(a.uploaded_at)})</span></span>
                <button className="btn-small" onClick={() => download(a.file_id, a.filename)} data-testid={`offb-attachment-download-${a.file_id}`}><Download size={13} /></button>
              </div>
            ))}
            {!closed && (
              <label className="btn-small" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer', marginTop: 4 }}>
                <Upload size={13} /> Upload evidence
                <input type="file" style={{ display: 'none' }} disabled={busy}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.eml,.msg,.txt"
                  onChange={e => { uploadFile(e.target.files[0]); e.target.value = ''; }}
                  data-testid="offb-attachment-input" />
              </label>
            )}

            {(c.escalations || []).length > 0 && (
              <>
                <h4 style={{ fontSize: 13, margin: '16px 0 6px', color: '#b45309' }}>HR Escalations (permanent record)</h4>
                {c.escalations.map(es => (
                  <div key={es.id} style={{ border: '1px solid #fcd34d', background: '#fffbeb', borderRadius: 8, padding: 10, fontSize: 12.5, marginBottom: 6 }} data-testid="offb-escalation-item">
                    <div><strong>{es.assets.length} asset(s)</strong> · total recovery {money(es.total_recovery_value)} · submitted by {es.escalated_by_name} · {fmtDT(es.escalated_at)}</div>
                    <div style={{ color: '#64748b' }}>{es.assets.map(f => `${f.asset_name} (${f.asset_tag}) — ${(f.status || '').replace('_', ' ')}`).join(', ')}</div>
                    <div style={{ marginTop: 4 }}>HR status: <strong data-testid="offb-escalation-hr-status">{es.hr_status === 'resolved' ? `Resolved by ${es.hr_resolved_by_name} · ${fmtDT(es.hr_resolved_at)}` : 'Open — awaiting HR resolution'}</strong></div>
                  </div>
                ))}
              </>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
              {!closed && (
                <button className="offb-btn offb-btn-primary" onClick={escalate}
                  disabled={busy || badFindings.length === 0}
                  title={badFindings.length === 0 ? 'Mark at least one asset Not Returned / Lost / Outstanding first' : ''}
                  data-testid="offb-escalate-btn">
                  <Send size={13} /> Notify HR / Escalate ({badFindings.length})
                </button>
              )}
              {!closed && (
                <button className="offb-btn offb-btn-clear" onClick={markCleared} disabled={busy || !canClear}
                  title={!canClear ? 'Unresolved assets — resolve the findings or escalate to HR first' : ''}
                  data-testid="offb-modal-clear-btn">
                  <CheckCircle size={13} /> Mark Cleared
                </button>
              )}
              <button className="offb-btn" style={{ color: '#334155', borderColor: '#cbd5e1' }} onClick={() => setShowHistory(v => !v)} data-testid="offb-history-toggle">
                <History size={13} /> {showHistory ? 'Hide' : 'Show'} Audit History ({detail.audit.length})
              </button>
            </div>

            {forcePanel && !closed && (
              <div style={{ marginTop: 12, border: '1px solid #fca5a5', background: '#fef2f2', borderRadius: 8, padding: 10, fontSize: 12.5 }} data-testid="offb-force-panel">
                <strong style={{ color: '#991b1b' }}>Force clearance exception — explicit authorization required</strong>
                <p style={{ margin: '4px 0 6px', color: '#7f1d1d' }}>
                  {outstandingAssets.length} asset(s) have no confirmed return and will remain assigned to {c.employee_name} (never marked Available).
                  Record why clearance is being completed anyway. Your name, role, time and reason are kept in the permanent audit trail.
                </p>
                <textarea className="offb-input" style={{ width: '100%', minHeight: 56 }} value={forceReason} maxLength={1000}
                  onChange={e => setForceReason(e.target.value)} placeholder="Authorization reason (min 10 characters) — e.g. HR approved recovery via final settlement, ticket #…"
                  data-testid="offb-force-reason" />
                <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                  <button className="offb-btn" style={{ color: '#991b1b', borderColor: '#fca5a5' }} onClick={forceClear}
                    disabled={busy || forceReason.trim().length < 10} data-testid="offb-force-confirm-btn">
                    <CheckCircle size={13} /> Authorize force clearance
                  </button>
                  <button className="offb-btn" style={{ color: '#334155', borderColor: '#cbd5e1' }} onClick={() => { setForcePanel(false); setForceReason(''); }} data-testid="offb-force-cancel-btn">Cancel</button>
                </div>
              </div>
            )}

            {showHistory && (
              <div style={{ marginTop: 10 }} data-testid="offb-audit-list">
                {detail.audit.map(a => (
                  <div key={a.id} style={{ borderLeft: '2px solid #cbd5e1', padding: '3px 0 6px 10px', fontSize: 12 }}>
                    <strong>{(a.action || '').replace(/offboarding_|_/g, ' ').trim()}</strong>
                    <span style={{ color: '#64748b' }}> — {a.performed_by_name} · {fmtDT(a.timestamp)}</span>
                    {a.details && <div style={{ color: '#475569' }}>{Object.entries(a.details).filter(([k, v]) => v !== null && v !== undefined && !['employee_id', 'employee_name'].includes(k)).map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join(' · ')}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
